import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeftIcon, ClockIcon } from "@heroicons/react/24/outline";
import { cardClass, linkButtonClass, useMediaQuery } from "../../Exams/TestBuilder/ui";
import { VERSION_PARAMS, VERSIONS_TAB } from "./QuestionTabs";
import useQuestionVersions from "./useQuestionVersions";
import VersionList from "./VersionList";
import VersionView from "./VersionView";
import VersionCompare from "./VersionCompare";
import { LoadError, versionNumber } from "./parts";

function ListSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" className={`${cardClass} lg:max-w-[24rem]`}>
      <span className="sr-only">{t("question_versions.loading")}</span>
      <div className="border-b border-slate-100 px-4 py-3 sm:px-5" aria-hidden="true">
        <span className="block h-5 w-40 rounded bg-slate-100 motion-safe:animate-pulse" />
      </div>
      <div className="divide-y divide-slate-100" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2 px-4 py-3 sm:px-5">
            <span className="block h-4 w-24 rounded bg-slate-100 motion-safe:animate-pulse" />
            <span className="block h-3 w-56 max-w-full rounded bg-slate-100 motion-safe:animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Blank({ title, text, className = "" }) {
  return (
    <div className={`${cardClass} flex-col items-center justify-center px-6 py-14 text-center ${className}`}>
      <span className="rounded-full bg-slate-100 p-3 text-slate-400">
        <ClockIcon className="h-6 w-6" aria-hidden="true" />
      </span>
      <h3 className="mt-3 text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 max-w-lg text-sm text-slate-500">{text}</p>
    </div>
  );
}

/**
 * The question page's Versions tab: the list of versions, one version open beside it (?v=3), or two compared
 * (?from=2&to=5). What is open lives in the address, which each step replaces: the top bar's Back leaves the page,
 * and from a version or a comparison the link at the top goes back to the list.
 */
export default function QuestionVersions({ uuid, className = "" }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Side by side from lg on; below it the list and what is open take turns.
  const wide = useMediaQuery("(min-width: 1024px)");
  const { versions, loading, error, retry, requestVersion, versionState } = useQuestionVersions(uuid);

  const open = versionNumber(searchParams.get("v"));
  const from = versionNumber(searchParams.get("from"));
  const to = versionNumber(searchParams.get("to"));
  const comparing = from !== null && to !== null;
  const view = comparing ? "compare" : open !== null ? "version" : "list";

  // The versions ticked for a comparison; back from one, its two stay ticked.
  const [ticked, setTicked] = useState(() => (comparing && from !== to ? [from, to] : []));
  useEffect(() => {
    if (comparing && from !== to) setTicked([Math.min(from, to), Math.max(from, to)]);
  }, [comparing, from, to]);
  const tick = useCallback(
    (number) =>
      setTicked((current) =>
        current.includes(number)
          ? current.filter((n) => n !== number)
          : // A third tick replaces the one ticked first.
            [...current.slice(-1), number]
      ),
    []
  );

  // After a step the user took, the focus goes to what it opened (the list's place may be hidden now).
  const pendingFocus = useRef(null);
  const listHeading = useRef(null);
  const detailHeading = useRef(null);
  useEffect(() => {
    const target = pendingFocus.current;
    pendingFocus.current = null;
    if (target === "list") listHeading.current?.focus();
    else if (target === "detail") detailHeading.current?.focus();
  }, [view, open, from, to]);

  const searchFor = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      VERSION_PARAMS.forEach((key) => params.delete(key));
      params.set("tab", VERSIONS_TAB);
      Object.entries(next).forEach(([key, value]) => params.set(key, String(value)));
      return `?${params.toString()}`;
    },
    [searchParams]
  );
  const show = (next, focus) => {
    pendingFocus.current = focus;
    setSearchParams(new URLSearchParams(searchFor(next)), { replace: true });
  };
  // From a button the focus goes to the comparison; from its own pickers it stays on the picker.
  const compare = (a, b, focus = "detail") => show({ from: Math.min(a, b), to: Math.max(a, b) }, focus);
  const backToList = () => show({}, "list");

  // A number from the address the question has no version of is not ticked.
  const tickedShown = ticked.filter((number) => versions.some((version) => version.number === number));
  const index = versions.findIndex((version) => version.number === open);
  const row = index >= 0 ? versions[index] : null;
  const latest = versions[0]?.number ?? null;

  let content;
  if (loading) {
    content = <ListSkeleton />;
  } else if (error) {
    const notFound = error?.response?.status === 404;
    content = (
      <LoadError
        className="max-w-2xl"
        message={t(notFound ? "question_versions.not_found" : "question_versions.load_failed")}
        onRetry={notFound ? undefined : retry}
      />
    );
  } else if (!versions.length) {
    content = <Blank className="flex" title={t("question_versions.empty_title")} text={t("question_versions.empty_text")} />;
  } else if (view === "compare") {
    content = (
      <VersionCompare
        versions={versions}
        from={from}
        to={to}
        versionState={versionState}
        request={requestVersion}
        onChange={(a, b) => compare(a, b, null)}
        headingRef={detailHeading}
      />
    );
  } else {
    content = (
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
        <VersionList
          className={view === "version" ? "hidden lg:block" : ""}
          versions={versions}
          open={open}
          ticked={tickedShown}
          onTick={tick}
          linkTo={(number) => `${pathname}${searchFor({ v: number })}`}
          onOpen={() => {
            pendingFocus.current = wide ? null : "detail";
          }}
          onCompare={compare}
          headingRef={listHeading}
        />
        {view === "version" ? (
          row ? (
            <VersionView
              key={row.number}
              row={row}
              state={versionState(row.number)}
              request={requestVersion}
              previous={versions[index + 1]?.number ?? null}
              latest={latest}
              // Beside the list (lg), whose own link compares these two: not offered twice.
              listPair={wide && versions.length > 1 ? [versions[1].number, latest] : null}
              onCompare={compare}
              headingRef={detailHeading}
            />
          ) : (
            <LoadError message={t("question_versions.version_not_found", { number: open })} />
          )
        ) : (
          <Blank className="hidden lg:flex" title={t("question_versions.pick_title")} text={t("question_versions.pick_text")} />
        )}
      </div>
    );
  }

  // From a version or a comparison, a quiet link goes one step up, to the list (the top bar's Back leaves the page).
  // From lg up an open version has the list beside it: there the link is left out (a comparison still takes it).
  const inside = !loading && !error && versions.length > 0 && (view === "compare" || (view === "version" && !wide));
  return (
    <div className={`space-y-4 motion-safe:animate-fade-in ${className}`}>
      {inside && (
        <button type="button" onClick={backToList} className={`${linkButtonClass} -ml-1.5 max-md:min-h-[2.75rem]`}>
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t("question_versions.back_to_list")}
        </button>
      )}
      {content}
    </div>
  );
}
